import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import apiClient from '../../lib/api-client'
import { toast } from '../../components/Toast/useToast'
import i18n from '../../i18n'
import type { components } from '@heliumtracker/api-types'

export type Note = components['schemas']['Note']
export type NotesPagination = components['schemas']['NotesPagination']

// Lesend/als Cache-Key ist origin_type polymorph — das Contract-Enum umfasst
// seit R2 auch 'User' (weitere Typen moeglich). Direkt aus den generierten
// api-types abgeleitet, damit kein Cast mehr noetig ist.
export type OriginType = NonNullable<Note['origin_type']>

// Beim ANLEGEN erlaubt das BE Hotspot, Contact, Host sowie User (nur der
// EIGENE Account, sonst 403 — siehe validate_origin_access im
// notes_controller). Story 5.1 erweitert den Typ um Host + User.
export type CreateOriginType = 'Hotspot' | 'Contact' | 'Host' | 'User'

/**
 * Kanonischer Textkoerper einer Note (Story 5.1): neue Clients lesen `body`,
 * `text` bleibt der Legacy-Alias mit identischem Wert.
 */
export function noteBody(note: Note): string {
  return note.body ?? note.text ?? ''
}

/**
 * Deep-Link-Ziel einer Note (Story 5.1): kanonisch `notable_path`, Fallback
 * auf den Legacy-Alias `origin_path`. Null fuer Typen ohne Detailseite (User).
 */
export function notePath(note: Note): string | null {
  return note.notable_path ?? note.origin_path ?? null
}

/**
 * Anzeigename der verknuepften Origin (Story 5.1): kanonisch `notable_label`,
 * Fallback auf den Legacy-Alias `origin_label`.
 */
export function noteLabel(note: Note): string | null {
  return note.notable_label ?? note.origin_label ?? null
}

type UseNotesArgs = {
  originType: OriginType
  originId: number
}

export function useNotes({ originType, originId }: UseNotesArgs) {
  return useQuery({
    queryKey: ['notes', originType, originId],
    queryFn: async () => {
      if (!Number.isFinite(originId)) {
        throw new Error(i18n.t('notes:toast.invalidOriginId'))
      }
      const { data, error } = await apiClient.GET('/api/v2/notes', {
        params: {
          query: {
            origin_type: originType,
            origin_id: originId,
          },
        },
      })
      if (error) throw new Error('FETCH_NOTES_FAILED')
      return (data?.data ?? []) as Note[]
    },
  })
}

type UseAllNotesArgs = {
  originType?: OriginType
  page?: number
}

type AllNotesResult = {
  notes: Note[]
  meta: NotesPagination | undefined
}

/**
 * Globale, unpaginierte-nach-origin Notes-Liste (fuer /notes-Uebersicht).
 * Anders als useNotes() ist origin_id hier NICHT erforderlich -- der
 * Backend-Endpoint (Api::V2::NotesController#index) liefert ohne Filter
 * bereits alle Notes des current_user, paginiert.
 */
export function useAllNotes({ originType, page = 1 }: UseAllNotesArgs = {}) {
  return useQuery({
    queryKey: ['notes', 'all', originType ?? 'ALL', page],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/v2/notes', {
        params: {
          query: {
            ...(originType ? { origin_type: originType } : {}),
            page,
          },
        },
      })
      if (error) throw new Error('FETCH_NOTES_FAILED')
      return {
        notes: (data?.data ?? []) as Note[],
        meta: data?.meta as NotesPagination | undefined,
      } as AllNotesResult
    },
  })
}

export function useCreateNote() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('notes')

  return useMutation({
    mutationFn: async (newNote: { origin_type: CreateOriginType, origin_id: number, title?: string, text: string }) => {
      if (!Number.isFinite(newNote.origin_id)) {
        throw new Error('INVALID_ORIGIN_ID')
      }
      // Story 5.1: neue Clients senden den kanonischen Contract
      // (notable_type/notable_id/body); das BE normalisiert auf origin_*/text.
      const { data, error } = await apiClient.POST('/api/v2/notes', {
        body: { note: { notable_type: newNote.origin_type, notable_id: newNote.origin_id, title: newNote.title, body: newNote.text } },
      })
      if (error) throw new Error('CREATE_NOTE_FAILED')
      return data?.data as Note
    },
    onMutate: async (newNote) => {
      await queryClient.cancelQueries({ queryKey: ['notes', newNote.origin_type, newNote.origin_id] })
      const previousNotes = queryClient.getQueryData<Note[]>(['notes', newNote.origin_type, newNote.origin_id])

      const optimisticNote = {
        id: Math.random(),
        ...newNote,
      } as Note

      queryClient.setQueryData<Note[]>(['notes', newNote.origin_type, newNote.origin_id], (old) => {
        return old ? [optimisticNote, ...old] : [optimisticNote]
      })

      return { previousNotes, originType: newNote.origin_type, originId: newNote.origin_id }
    },
    onError: (err, _newNote, context) => {
      if (context?.previousNotes) {
        queryClient.setQueryData(['notes', context.originType, context.originId], context.previousNotes)
      }
      toast.error(err.message === 'INVALID_ORIGIN_ID' ? t('toast.invalidOriginId') : t('toast.createError'))
    },
    onSuccess: () => {
      toast.success(t('toast.created'))
    },
    onSettled: () => {
      // Breite Invalidierung (nur queryKey[0]==='notes' als Praefix) statt nur
      // des Origin-scoped Keys: sonst bleibt die globale /notes-Uebersicht
      // (['notes','all',...]) stale, wenn die Mutation aus einem Origin-Widget
      // (Hotspot-/Contact-Detail) ausgeloest wurde -- und umgekehrt.
      queryClient.invalidateQueries({ queryKey: ['notes'] })
    },
  })
}

export function useUpdateNote() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('notes')

  return useMutation({
    mutationFn: async (updatedNote: { id: number, origin_type: OriginType, origin_id: number, title?: string, text: string }) => {
      const { data, error } = await apiClient.PATCH('/api/v2/notes/{id}', {
        params: { path: { id: updatedNote.id } },
        // Story 5.1: kanonischer Textkoerper-Name `body` (Alias von text).
        body: { note: { title: updatedNote.title, body: updatedNote.text } },
      })
      if (error) throw new Error('UPDATE_NOTE_FAILED')
      return data?.data as Note
    },
    onMutate: async (updatedNote) => {
      await queryClient.cancelQueries({ queryKey: ['notes', updatedNote.origin_type, updatedNote.origin_id] })
      const previousNotes = queryClient.getQueryData<Note[]>(['notes', updatedNote.origin_type, updatedNote.origin_id])

      queryClient.setQueryData<Note[]>(['notes', updatedNote.origin_type, updatedNote.origin_id], (old) => {
        if (!old) return []
        return old.map(n => n.id === updatedNote.id ? { ...n, ...updatedNote } as Note : n)
      })

      return { previousNotes, originType: updatedNote.origin_type, originId: updatedNote.origin_id }
    },
    onError: (_err, _updatedNote, context) => {
      if (context?.previousNotes) {
        queryClient.setQueryData(['notes', context.originType, context.originId], context.previousNotes)
      }
      toast.error(t('toast.updateError'))
    },
    onSuccess: () => {
      toast.success(t('toast.updated'))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] })
    },
  })
}

export function useDeleteNote() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('notes')

  return useMutation({
    mutationFn: async ({ id }: { id: number, origin_type: OriginType, origin_id: number }) => {
      const { error } = await apiClient.DELETE('/api/v2/notes/{id}', {
        params: { path: { id } },
      })
      if (error) throw new Error('DELETE_NOTE_FAILED')
    },
    onMutate: async ({ id, origin_type, origin_id }) => {
      await queryClient.cancelQueries({ queryKey: ['notes', origin_type, origin_id] })
      const previousNotes = queryClient.getQueryData<Note[]>(['notes', origin_type, origin_id])

      queryClient.setQueryData<Note[]>(['notes', origin_type, origin_id], (old) => {
        if (!old) return []
        return old.filter(n => n.id !== id)
      })

      return { previousNotes, originType: origin_type, originId: origin_id }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousNotes) {
        queryClient.setQueryData(['notes', context.originType, context.originId], context.previousNotes)
      }
      toast.error(t('toast.deleteError'))
    },
    onSuccess: () => {
      toast.success(t('toast.deleted'))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] })
    },
  })
}
